import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Users, Flame, Globe2, ChevronRight } from 'lucide-react';
import Navbar from '../common/Navbar';
import Footer from '../common/Footer';
import SEO from '../common/SEO';

import aboutSheetLeft from '../../assets/about/about-sheet-left-element.webp';
import aboutSheetRight from '../../assets/about/about-sheet-right-element.webp';
import pillarLogo from '../../assets/about/logo-and-pillar-element-transparent.png';

import '../../styles/AboutPage.css';

const PILLARS = [
  {
    id: 'ownership',
    title: '100% Ownership & High Royalties',
    badge: 'Up to 97.5% Royalties',
    icon: <ShieldCheck size={36} color="#d9fb06" />,
    smallIcon: <ShieldCheck size={22} color="#d9fb06" />,
    desc: 'Artists maintain full ownership and master rights of their creations. Earn up to 97.5% royalties paid directly to your account.',
    subtitle: 'The foundational principles guiding every tool, service, and policy we build.',
  },
  {
    id: 'ecosystem',
    title: 'Collaborative Multi-Role Ecosystem',
    badge: '4 Integrated Creator Roles',
    icon: <Users size={36} color="#d9fb06" />,
    smallIcon: <Users size={22} color="#d9fb06" />,
    desc: 'From vocalists and music producers to video directors and cover designers, SenritsuX bridges the gap between complementary creators.',
    subtitle: 'The foundational principles guiding every tool, service, and policy we build.',
  },
  {
    id: 'services',
    title: 'Artist Services & Release Management',
    badge: 'Direct Release Tools',
    icon: <Flame size={36} color="#d9fb06" />,
    smallIcon: <Flame size={22} color="#d9fb06" />,
    desc: 'Monetize your craft directly. Manage releases, coordinate collaborations, pitch to playlists, and protect your royalties without middleman fees.',
    subtitle: 'The foundational principles guiding every tool, service, and policy we build.',
  },
  {
    id: 'reach',
    title: 'Global Reach & Artist WEB',
    badge: '150+ Digital Platforms',
    icon: <Globe2 size={36} color="#d9fb06" />,
    smallIcon: <Globe2 size={22} color="#d9fb06" />,
    desc: 'Distribute across Spotify, Apple Music, and 150+ platforms while building a verifiable public portfolio on Artist WEB.',
    subtitle: 'The foundational principles guiding every tool, service, and policy we build.',
  },
];

export default function AboutPage() {
  // CRITICAL: Single source of truth for active option, highlighted selection, and displayed details
  const [selectedPillarId, setSelectedPillarId] = useState('ownership');

  const activePillar = PILLARS.find((p) => p.id === selectedPillarId) || PILLARS[0];

  return (
    <div className="sx-about-root">
      <SEO
        title="About SenritsuX | Music Distribution & Artist Services"
        description="Learn about SenritsuX, an independent music platform built to help artists distribute music, manage releases, and build their creative careers."
        canonical="/about"
        keywords={[
          'about SenritsuX',
          'independent music platform',
          'music distribution platform',
          'artist services',
          'independent artists',
        ]}
      />

      <Navbar />

      <main className="sx-about-main">
        {/* ================================================================
            SECTION 1: HERO & ABOUT SHEETS (Matches 04.webp)
            ================================================================ */}
        <section className="sx-about-hero-section" aria-label="About SenritsuX Overview">
          {/* Left Column: Heading, Subtitle & Left Sheet */}
          <div className="sx-about-hero-left">
            <div className="sx-about-hero-header">
              <h1 className="sx-about-title">
                Built for <span className="sx-about-title-accent">Artists,</span>
                <br />
                Powered by <span className="sx-about-title-accent">Creators</span>
              </h1>
              <p className="sx-about-subtitle">
                SenritsuX is an independent music distribution and artist services
                platform designed to liberate independent music artists. We provide
                global distribution, release tools with a steadfast{' '}
                <strong>Artist-First</strong> mindset.
              </p>
            </div>

            {/* Left Sheet Card (about-sheet-left-element.webp) with 3-tier cover-up and real HTML text */}
            <div className="sx-about-sheet-left-wrap">
              <img
                src={aboutSheetLeft}
                alt=""
                aria-hidden="true"
                className="sx-about-sheet-left-img"
              />
              <div className="sx-about-sheet-left-overlay">
                {/* Tier 2: Matching cover-up sheet to prevent original text bleed */}
                <div className="sx-about-sheet-left-coverup" aria-hidden="true" />
                {/* Tier 3: Sharp accessible replacement content */}
                <div className="sx-about-sheet-left-content">
                  <h2 className="sx-about-sheet-left-heading">
                    Ready to Take Your Sound to the World?
                  </h2>
                  <p className="sx-about-sheet-left-paragraph">
                    Join thousands of creators who trust SenritsuX for distribution, collaboration,
                    <br className="sx-hide-mobile" />
                    and growth.
                  </p>
                  <div className="sx-about-sheet-left-btns">
                    <Link
                      to="/signup"
                      className="sx-about-btn-signup"
                      aria-label="Get Started Now"
                    >
                      Get Started Now →
                    </Link>
                    <Link
                      to="/services"
                      className="sx-about-btn-services"
                      aria-label="Explore Services"
                    >
                      Explore Services
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Mission and 4 Creator Cards with real sharp HTML text */}
          <div className="sx-about-hero-right">
            <div className="sx-about-sheet-right-wrap">
              <img
                src={aboutSheetRight}
                alt=""
                aria-hidden="true"
                className="sx-about-sheet-right-img"
              />
              <div className="sx-about-sheet-right-overlay">
                {/* Top Right Card: Mission with 3-tier layering */}
                <div className="sx-about-right-mission">
                  {/* Tier 2: Mission cover-up sheet */}
                  <div className="sx-about-mission-coverup" aria-hidden="true" />
                  {/* Tier 3: Mission content */}
                  <div className="sx-about-mission-content">
                    <span className="sx-about-mission-badge">OUR MISSION</span>
                    <h2 className="sx-about-mission-heading">Democratizing the Music Industry</h2>
                    <p className="sx-about-mission-text">
                      The traditional music business has historically created friction for
                      independent talent-unfavorable contracts, opaque royalties, and
                      fragmented tools. SenritsuX was founded to dismantle these barriers.
                    </p>
                    <p className="sx-about-mission-text">
                      Whether you are releasing your very first single or managing an established
                      catalog, our platform offers the infrastructure, exposure, and commercial
                      freedom to turn your passion into a sustainable career.
                    </p>
                  </div>
                </div>

                {/* Bottom 4 Cards: Creator Roles with 3-tier layering */}
                <div className="sx-about-right-roles">
                  <div className="sx-about-role-card sx-role-1">
                    <div className="sx-about-role-coverup" aria-hidden="true" />
                    <div className="sx-about-role-content">
                      <h3 className="sx-about-role-title">Singers & Rappers</h3>
                      <p className="sx-about-role-desc">
                        Release tracks globally, get pitched to top playlists, build your presence, and commission visual artists for cover art and music videos.
                      </p>
                    </div>
                  </div>
                  <div className="sx-about-role-card sx-role-2">
                    <div className="sx-about-role-coverup" aria-hidden="true" />
                    <div className="sx-about-role-content">
                      <h3 className="sx-about-role-title">Music Producers</h3>
                      <p className="sx-about-role-desc">
                        Collaborate with vocalists, produce tracks for global distribution, manage production credits, and receive direct payouts.
                      </p>
                    </div>
                  </div>
                  <div className="sx-about-role-card sx-role-3">
                    <div className="sx-about-role-coverup" aria-hidden="true" />
                    <div className="sx-about-role-content">
                      <h3 className="sx-about-role-title">Video Editors</h3>
                      <p className="sx-about-role-desc">
                        Produce official music videos, visualizers, and promo reels for artists across genres.
                      </p>
                    </div>
                  </div>
                  <div className="sx-about-role-card sx-role-4">
                    <div className="sx-about-role-coverup" aria-hidden="true" />
                    <div className="sx-about-role-content">
                      <h3 className="sx-about-role-title">Graphic Designers</h3>
                      <p className="sx-about-role-desc">
                        Design high-converting album artwork, Spotify canvases, and promotional posters for musicians.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ================================================================
            SECTION 2: FOUR PILLARS INTERACTIVE SECTION (Matches 05.webp)
            ================================================================ */}
        <section className="sx-about-pillars-section" aria-label="Four Pillars of SenritsuX">
          {/* Master 2-Column Interactive Container */}
          <div className="sx-about-pillars-grid">
            {/* Left Column: Fixed-Size Option Detail Card (Moved 40px down) */}
            <div className="sx-about-pillar-display">
              <div
                className="sx-about-detail-card"
                role="region"
                aria-live="polite"
                aria-label={`${activePillar.title} details`}
              >
                <div key={activePillar.id} className="sx-about-detail-content">
                  <div className="sx-about-detail-icon-wrap">
                    {activePillar.icon}
                  </div>
                  <h3 className="sx-about-detail-title">{activePillar.title}</h3>
                  <p className="sx-about-detail-desc">{activePillar.desc}</p>
                </div>
              </div>

              <p className="sx-about-detail-subtitle">
                {activePillar.subtitle}
              </p>
            </div>

            {/* Right Column: Logo Header + Staggered Selectable Pillars List */}
            <div className="sx-about-pillar-right-col">
              {/* Four Pillars Logo Header (Aligned with staggered options) */}
              <div className="sx-about-pillars-header">
                <img
                  src={pillarLogo}
                  alt="Four Pillars of SenritsuX"
                  className="sx-about-pillars-logo-img"
                />
              </div>

              {/* Options List (Staggered progressive offsets & successive 5% scaling) */}
              <div
                className="sx-about-pillar-options"
                role="tablist"
                aria-label="Four Pillars Options"
              >
                {PILLARS.map((pillar, idx) => {
                  const isSelected = pillar.id === selectedPillarId;
                  return (
                    <button
                      key={pillar.id}
                      type="button"
                      role="tab"
                      aria-selected={isSelected}
                      aria-controls={`pillar-panel-${pillar.id}`}
                      id={`pillar-tab-${pillar.id}`}
                      className={`sx-about-option-item sx-opt-${idx + 1} ${isSelected ? 'active' : ''}`}
                      onClick={() => setSelectedPillarId(pillar.id)}
                    >
                      <div className="sx-about-option-left">
                        <div className="sx-about-option-icon-wrap">
                          {pillar.smallIcon}
                        </div>
                        <span className="sx-about-option-title">{pillar.title}</span>
                      </div>
                      <ChevronRight size={18} className="sx-about-option-arrow" />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
